import React, { useEffect, useState } from 'react';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { PayApplicationData, RequestForChangeData } from './DocumentIndexData';
import { Order } from './DocumentIndexUtils';
import { GreenCheck } from '../custom-components/CustomCheckboxes';
import { useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { getDocumentsType } from '../../features/documents/selectors';
import { DocumentTemplateType } from '../../api-client/autogenerated';
import { PunchListLocationRow } from './DocumentIndex';
import { formatDays, formatMoney, PAY_APPLICATION_COUNTED_STATUSES } from '../../scripts/utils';
import { Info, InfoOutlined } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import { Tooltip } from '@material-ui/core';

const useStyles = makeStyles(() =>
  createStyles({
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    headerText: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '12px',
      // LEX LOOK HERE. PERHAPS CHANGE lineHeight IF FIRST ELEMENT OF columnTitles IS SUBMITTAL #
      lineHeight: '22px',
      textTransform: 'uppercase',
    },
    disableDefaultSortIcon: {
      display: 'none',
    },
    sortIcon: {
      display: 'flex',
      flexDirection: 'column',
      height: '22px',
      paddingTop: '1px',
      marginLeft: '8px',
    },
    sortIconEnabled: {
      color: '#616061',
    },
    sortIconDisabled: {
      color: '#B2B1B2',
    },
    stickyLeftCell: {
      position: 'sticky',
      // Above the plain sticky-header cells (MUI's own `stickyHeader` class uses zIndex 2) so this
      // cell's content stays visible as other header cells slide underneath it while scrolling.
      zIndex: 3,
      // Matches MUI's default `background.default` (#fafafa), which is what the other header
      // cells render via the `stickyHeader` class -- kept explicit since this class must win over
      // it for z-index/position anyway.
      backgroundColor: '#FAFAFA',
    },
  }),
);

export interface HeadCell<T> {
  id: keyof T & (number | string);
  label: string;
  width?: string;
  noSort?: boolean;
  tooltip?: string;
}

interface DocumentIndexHeaderProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  notHiddenRowCount: number;
  filteredRows: any[];
  columnTitles: HeadCell<any>[];
  isPlanholderList?: boolean;
  isUserTable?: boolean;
  hideCheckboxes?: boolean;
  isCalendar?: boolean;
  /** Ref attached to the checkbox header cell, used to measure its rendered width. */
  checkboxCellRef?: React.Ref<HTMLTableCellElement>;
  /** Left offset (px) for the sticky document-number column, measured off the checkbox column. */
  numberColumnLeft?: number;
}

const DocumentIndexHeader = (props: DocumentIndexHeaderProps) => {
  const classes = useStyles();
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    notHiddenRowCount,
    filteredRows,
    onRequestSort,
    columnTitles,
    isPlanholderList = false,
    isUserTable = false,
    hideCheckboxes = false,
    isCalendar = false,
    checkboxCellRef,
    numberColumnLeft = 0,
  } = props;

  const hasCheckboxColumn = !(hideCheckboxes || isPlanholderList);

  const selectedProject = useSelector(getProjectState);
  const docType = useSelector(getDocumentsType);

  const [totalCost, setTotalCost] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [totalRetainage, setTotalRetainage] = useState(0);

  useEffect(() => {
    if (docType === DocumentTemplateType.PunchList) {
      const filteredItems = (filteredRows as Omit<PunchListLocationRow, 'locationObject'>[])
        .map((item) => item.children)
        .flat();
      const cost = filteredItems.reduce<number>((prev, cur) => prev + cur?.cost || 0, 0);
      setTotalCost(cost);
    } else if (
      docType === DocumentTemplateType.RequestsForChange ||
      docType === DocumentTemplateType.PotentialChangeOrders
    ) {
      const itemsCost = (filteredRows as RequestForChangeData[]).map((item) => item.cost ?? 0);
      const cost = itemsCost.reduce((prev, cur) => prev + cur, 0);
      setTotalCost(cost);

      const itemsTime = (filteredRows as RequestForChangeData[]).map((item) => item.time ?? 0);
      const time = itemsTime.reduce((prev, cur) => prev + cur, 0);
      setTotalTime(time);
    } else if (docType === DocumentTemplateType.PayApplications) {
      // Only final pay applications whose review has finished count toward the totals.
      const countedItems = (filteredRows as PayApplicationData[]).filter(
        (item) => item.isFinal && PAY_APPLICATION_COUNTED_STATUSES.includes(item.status),
      );
      const total = countedItems.reduce((prev, cur) => prev + (cur.dollarValue ?? 0), 0);
      setTotalCost(total);

      const retainage = countedItems.reduce((prev, cur) => prev + (cur.retainage ?? 0), 0);
      setTotalRetainage(retainage);
    }
  }, [filteredRows]);

  const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  const getWidthConstraint = (index: number) => {
    if (index === 2) {
      return 140;
    }
    if (index === 3) {
      return 120;
    }
    if (index === 7) {
      return 100;
    }
    return undefined;
  };

  const showCostValue = (cell: HeadCell<any>) => {
    if (!docType || filteredRows.length === 0) return false;
    if (docType === DocumentTemplateType.PayApplications) {
      return cell.id === 'dollarValue';
    }
    return (
      [
        DocumentTemplateType.PunchList,
        DocumentTemplateType.RequestsForChange,
        DocumentTemplateType.PotentialChangeOrders,
      ].includes(docType) && cell.id === 'cost'
    );
  };

  const showRetainageValue = (cell: HeadCell<any>) => {
    return (
      docType === DocumentTemplateType.PayApplications &&
      cell.id === 'retainage' &&
      filteredRows.length > 0
    );
  };

  const showTimeValue = (cell: HeadCell<any>) => {
    return (
      !!docType &&
      [DocumentTemplateType.RequestsForChange, DocumentTemplateType.PotentialChangeOrders].includes(
        docType,
      ) &&
      cell.id === 'time' &&
      filteredRows.length > 0
    );
  };

  const shouldNotWrap = (cell: HeadCell<any>) => {
    return cell.label.includes('#') || showCostValue(cell) || showRetainageValue(cell);
  };

  const isSubmittal = columnTitles[0] ? columnTitles[0].label === 'Submittal #' : false;
  const isPunchList = docType === DocumentTemplateType.PunchList;

  return (
    <TableHead>
      <TableRow
        style={{
          borderTop: !isCalendar ? '1px solid #EDECEC' : '',
          borderBottom: isCalendar ? '1px solid #EDECEC' : '',
          height: isSubmittal ? 70 : undefined,
        }}
      >
        {hideCheckboxes || isPlanholderList ? null : (
          <TableCell
            ref={checkboxCellRef}
            padding="checkbox"
            className={classes.stickyLeftCell}
            style={{
              border: 'none',
              width: '1%',
              left: 0,
              paddingLeft:
                docType === DocumentTemplateType.Task || docType === DocumentTemplateType.CaTask
                  ? 6
                  : 0,
            }}
          >
            <GreenCheck
              indeterminate={numSelected > 0 && numSelected < notHiddenRowCount}
              checked={
                isUserTable
                  ? notHiddenRowCount > 1 && numSelected === notHiddenRowCount - 1
                  : notHiddenRowCount > 0 && numSelected === notHiddenRowCount
              }
              onChange={onSelectAllClick}
            />
          </TableCell>
        )}
        {columnTitles.map((headCell, index) => {
          if (!selectedProject?.isProcoreIntegrationEnabled && headCell.id === 'sync') return <></>;
          if (headCell.label === 'HIDE') return <></>;
          return (
            <TableCell
              key={`${headCell.id}-${headCell.label}`}
              align="left"
              sortDirection={orderBy === headCell.id ? order : false}
              className={index === 0 ? classes.stickyLeftCell : undefined}
              style={{
                border: 'none',
                paddingLeft: isPunchList && index === 0 ? 64 : undefined,
                paddingRight: isSubmittal && (index === 0 || index === 1) ? 16 : undefined,
                width: headCell.width ?? '100%',
                whiteSpace: shouldNotWrap(headCell) ? 'nowrap' : 'normal',
                left: index === 0 ? (hasCheckboxColumn ? numberColumnLeft : 0) : undefined,
              }}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                classes={{
                  root: classes.headerText,
                  icon: classes.disableDefaultSortIcon,
                }}
                // Using inline style here because other approaches won't overwrite color
                style={{
                  color: '#616061',
                  minWidth: isSubmittal ? getWidthConstraint(index) : undefined,
                }}
              >
                {headCell.label}
                {headCell.tooltip ? (
                  <Tooltip
                    arrow
                    placement="top"
                    title={headCell.tooltip}
                    PopperProps={{ style: { paddingLeft: 4 } }}
                  >
                    <IconButton style={{ padding: '0 0 0 8px', cursor: 'default' }}>
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {headCell.label !== '' && !headCell.noSort ? (
                  <div className={classes.sortIcon}>
                    <FontAwesomeIcon
                      icon={faCaretUp}
                      style={{ marginBottom: '-3px' }}
                      className={
                        orderBy === headCell.id ? classes.sortIconEnabled : classes.sortIconDisabled
                      }
                    />
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      style={{ marginTop: '-3px' }}
                      className={
                        orderBy === headCell.id ? classes.sortIconEnabled : classes.sortIconDisabled
                      }
                    />
                  </div>
                ) : null}

                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
              {showCostValue(headCell) && (
                <p style={{ color: '#464546', fontWeight: 700, margin: 0 }}>
                  {formatMoney(totalCost)}
                </p>
              )}
              {showRetainageValue(headCell) && (
                <p style={{ color: '#464546', fontWeight: 700, margin: 0 }}>
                  {formatMoney(totalRetainage)}
                </p>
              )}
              {showTimeValue(headCell) && (
                <p style={{ color: '#464546', fontWeight: 700, margin: 0 }}>
                  {formatDays(totalTime)}
                </p>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

export default DocumentIndexHeader;
